import type {
  IChartApi,
  MouseEventParams,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  SeriesType,
  Time,
  UTCTimestamp
} from "lightweight-charts"

// TradingView's long / short position tool, drawn under the candles of one price series
// (docs/design/0016): a box from the entry price, green to the target and red to the stop.
// A strategy's trades (strategies/tradeBoxes.ts) and the followed paper account's protected
// positions (trading/TradingLayer.ts) use it.

export interface TradeBox {
  readonly from: UTCTimestamp
  readonly to: UTCTimestamp
  /** Bars the box reaches past `to` (an open position's runs on a little into the future). */
  readonly extend?: number
  readonly entry: number
  /** Where it exited (an open position: the current price): a dashed line from the entry at `from` to it at `to`. */
  readonly exit: number | null
  /** An open position's direction: the band from the entry to `exit` shows its running profit (green) or loss (red). */
  readonly long?: boolean
  /** Price ranges shaded green (reward) and red (risk); null when not drawn. */
  readonly reward: number | null
  readonly risk: number | null
  /** The nearer take-profits of several (docs/design/0017): a line across the green zone at each. */
  readonly targets?: ReadonlyArray<number>
  /** Text on the box (TradingView's), shown while the pointer is on it: at the target's and the stop's outer edge, and lines at the entry. */
  readonly labels?: BoxLabels
}

export interface BoxLabels {
  readonly reward: string | null
  /** Several take-profits: a label at each one's line instead of `reward`. */
  readonly targets: ReadonlyArray<{ readonly price: number; readonly text: string }>
  readonly risk: string | null
  readonly entry: ReadonlyArray<string>
  /** The entry label's colour: the running profit (green) or loss (red). */
  readonly entryWinning: boolean
}

const REWARD = "rgba(38, 166, 154, 0.22)"
const RISK = "rgba(242, 54, 69, 0.22)"
const RUNNING_PROFIT = "rgba(38, 166, 154, 0.45)"
const RUNNING_LOSS = "rgba(242, 54, 69, 0.45)"
const ENTRY = "rgba(178, 181, 190, 0.7)"
const PATH = "rgba(178, 181, 190, 0.9)"
const TARGET_LINE = "rgba(38, 166, 154, 0.9)"
const LABEL_PROFIT = "#089981"
const LABEL_LOSS = "#f23645"
const LABEL_FONT = "11px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
const LABEL_LINE = 14
const LABEL_PAD = 5

type Target = Parameters<IPrimitivePaneRenderer["draw"]>[0]

/** The boxes on one price series, drawn under the candles. */
export class TradeBoxesPrimitive implements ISeriesPrimitive<Time> {
  private boxes: ReadonlyArray<TradeBox> = []
  private chart: IChartApi | null = null
  private series: ISeriesApi<SeriesType> | null = null
  private requestUpdate: (() => void) | null = null
  /** The pointer on the pane: a box under it shows its labels. */
  private pointer: { readonly x: number; readonly y: number } | null = null
  private readonly onMove = (param: MouseEventParams<Time>) => {
    const next = param.point ?? null
    if (next === null && this.pointer === null) return
    this.pointer = next
    if (this.boxes.some((box) => box.labels !== undefined)) this.requestUpdate?.()
  }
  private readonly view: IPrimitivePaneView = {
    zOrder: () => "bottom",
    renderer: () => ({ draw: (target) => this.draw(target) })
  }
  /** The labels, over the candles. */
  private readonly labelView: IPrimitivePaneView = {
    zOrder: () => "top",
    renderer: () => ({ draw: (target) => this.drawLabels(target) })
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chart = param.chart
    this.series = param.series
    this.requestUpdate = param.requestUpdate
    param.chart.subscribeCrosshairMove(this.onMove)
  }

  detached(): void {
    this.chart?.unsubscribeCrosshairMove(this.onMove)
    this.chart = null
    this.series = null
    this.requestUpdate = null
  }

  setBoxes(boxes: ReadonlyArray<TradeBox>): void {
    this.boxes = boxes
    this.requestUpdate?.()
  }

  paneViews(): ReadonlyArray<IPrimitivePaneView> {
    return [this.view, this.labelView]
  }

  /** A box's horizontal span on the pane (media pixels), or null when its bars aren't on the chart. */
  private span(box: TradeBox): { readonly left: number; readonly right: number } | null {
    const timeScale = this.chart!.timeScale()
    const x1 = timeScale.timeToCoordinate(box.from)
    const x2 = timeScale.timeToCoordinate(box.to)
    if (x1 === null || x2 === null) return null
    const bar = timeScale.options().barSpacing
    return { left: x1, right: x1 + Math.max(x2 - x1 + (box.extend ?? 0) * bar, bar) }
  }

  /** Whether the pointer is on the box: within its bars, between its far edges. */
  private hovered(box: TradeBox, span: { readonly left: number; readonly right: number }, yEntry: number): boolean {
    const pointer = this.pointer
    const series = this.series
    if (pointer === null || series === null || pointer.x < span.left - 3 || pointer.x > span.right + 3) return false
    const ys = [yEntry, ...[box.reward, box.risk, box.exit].map((price) => (price === null ? null : series.priceToCoordinate(price)))]
      .filter((y) => y !== null)
    return pointer.y >= Math.min(...ys) - 3 && pointer.y <= Math.max(...ys) + 3
  }

  private drawLabels(target: Target): void {
    const series = this.series
    if (this.chart === null || series === null) return
    target.useMediaCoordinateSpace(({ context, mediaSize }) => {
      context.font = LABEL_FONT
      context.textBaseline = "middle"
      context.textAlign = "center"
      // A rounded tag of text lines, centred at x, with its top at y (clamped inside the pane).
      const tag = (lines: ReadonlyArray<string>, x: number, y: number, fill: string, border: boolean) => {
        const width = Math.max(...lines.map((line) => context.measureText(line).width)) + 2 * LABEL_PAD
        const height = lines.length * LABEL_LINE + 4
        const left = Math.min(Math.max(2, x - width / 2), mediaSize.width - width - 2)
        const top = Math.min(Math.max(2, y), mediaSize.height - height - 2)
        context.fillStyle = fill
        context.beginPath()
        context.roundRect(left, top, width, height, 3)
        context.fill()
        if (border) {
          context.strokeStyle = "#ffffff"
          context.lineWidth = 1
          context.stroke()
        }
        context.fillStyle = "#ffffff"
        lines.forEach((line, i) => context.fillText(line, left + width / 2, top + 2 + LABEL_LINE * (i + 0.5)))
        return height
      }
      for (const box of this.boxes) {
        const labels = box.labels
        const span = labels === undefined ? null : this.span(box)
        const yEntry = series.priceToCoordinate(box.entry)
        if (labels === undefined || span === null || yEntry === null) continue
        // Only the box the pointer is on (the user's choice: the chart stays clean).
        if (span.right < 0 || span.left > mediaSize.width || !this.hovered(box, span, yEntry)) continue
        const middle = (span.left + span.right) / 2
        // Outside each zone's far edge: above it when the zone is above the entry, else below.
        const edge = (price: number | null, text: string | null, fill: string) => {
          const y = price === null || text === null ? null : series.priceToCoordinate(price)
          if (y === null || text === null) return
          tag([text], middle, y < yEntry ? y - LABEL_LINE - 6 : y + 2, fill, false)
        }
        edge(box.reward, labels.reward, LABEL_PROFIT)
        edge(box.risk, labels.risk, LABEL_LOSS)
        for (const target of labels.targets) edge(target.price, target.text, LABEL_PROFIT)
        if (labels.entry.length > 0) {
          tag(labels.entry, middle, yEntry - (labels.entry.length * LABEL_LINE + 4) / 2, labels.entryWinning ? LABEL_PROFIT : LABEL_LOSS, true)
        }
      }
    })
  }

  private draw(target: Target): void {
    const chart = this.chart
    const series = this.series
    if (chart === null || series === null || this.boxes.length === 0) return
    const timeScale = chart.timeScale()
    // A trade on one bar still shows as a bar wide.
    const minWidth = timeScale.options().barSpacing
    target.useBitmapCoordinateSpace(({ context, horizontalPixelRatio: h, verticalPixelRatio: v }) => {
      for (const box of this.boxes) {
        const x1 = timeScale.timeToCoordinate(box.from)
        const x2 = timeScale.timeToCoordinate(box.to)
        const yEntry = series.priceToCoordinate(box.entry)
        if (x1 === null || x2 === null || yEntry === null) continue
        const left = Math.round(x1 * h)
        const width = Math.round(Math.max(x2 - x1 + (box.extend ?? 0) * minWidth, minWidth) * h)
        // Up to `to`: where the price has got to so far.
        const pathWidth = Math.round(Math.max(x2 - x1, minWidth) * h)
        const zone = (price: number | null, color: string, zoneWidth = width) => {
          if (price === null) return
          const y = series.priceToCoordinate(price)
          if (y === null) return
          context.fillStyle = color
          context.fillRect(left, Math.round(Math.min(y, yEntry) * v), zoneWidth, Math.max(1, Math.round(Math.abs(y - yEntry) * v)))
        }
        zone(box.reward, REWARD)
        zone(box.risk, RISK)
        for (const price of box.targets ?? []) {
          const y = series.priceToCoordinate(price)
          if (y === null) continue
          context.fillStyle = TARGET_LINE
          context.fillRect(left, Math.round(y * v), width, Math.max(1, Math.round(v)))
        }
        if (box.long !== undefined && box.exit !== null) {
          const winning = (box.exit - box.entry) * (box.long ? 1 : -1) >= 0
          zone(box.exit, winning ? RUNNING_PROFIT : RUNNING_LOSS, pathWidth)
        }
        context.fillStyle = ENTRY
        context.fillRect(left, Math.round(yEntry * v), width, Math.max(1, Math.round(v)))
        const yExit = box.exit === null ? null : series.priceToCoordinate(box.exit)
        if (yExit === null) continue
        context.save()
        context.strokeStyle = PATH
        context.lineWidth = Math.max(1, Math.round(h))
        context.setLineDash([4 * h, 4 * h])
        context.beginPath()
        context.moveTo(left, yEntry * v)
        context.lineTo(left + pathWidth, yExit * v)
        context.stroke()
        context.restore()
      }
    })
  }
}
